<template>
  <div class="entries-box relative mr-2">
    <div class="entries-box __blur"></div>
    <div
      class="entries-box __header horizontal layout center"
      :class="{black: slide.textColour === '#ffffff'}"
    >
      <div class="flex entries-count">
        {{ $t('entries') }}
        <span>{{ countWheelEntries | formatTotalPoint }}</span>
      </div>
      <div
        v-if="isShowUpdateWheelNotice"
        class="entries-box __reset horizontal layout center"
      >
        <div
          v-if="pendingEntriesCount"
          v-tooltip="`${pendingEntriesCount} have entered since the last spin.<br />
          Click “Update Wheel” to add them.`"
          class="pending-count new-audience horizontal layout center"
        >
          <span v-if="pendingEntriesCount"></span>
          {{ pendingEntriesCount | formatTotalPoint }}
        </div>
        <div
          v-if="oldEntryFromAudienceCount"
          v-tooltip="`${oldEntryFromAudienceCount} have gone inactive since the last spin.<br />
          Click “Update Wheel” to remove them.`"
          class="pending-count horizontal layout center"
        >
          <span v-if="oldEntryFromAudienceCount"></span>
          {{ oldEntryFromAudienceCount | formatTotalPoint }}
        </div>

        <div class="horizontal layout center btn-update-wheel" @click="openPopupUpdateWheel()">
          {{ $t('updateWheelButton') }}
        </div>
      </div>
    </div>
    <div
      class="entries-box __input-entry"
    >
      <div
        class="relative-entry-box"
        :class="{
          expanded: isInputExpanded
        }"
      >
        <aha-textarea
          v-model="newEntry"
          :placeholder="entriesPlaceholder"
          :isDisabled="isInputDisabled"
          :classes="variant === 'white' ? 'light' : 'dark'"
          :class="{'disabled': isInputDisabled}"
          @focus="onFocusEntryInput"
          @blur="onBlurEntryInput"
        >
        </aha-textarea>
        <div class="actions py-2">
          <span
            class="actions-secondary font-bold text-sm mr-3"
            :class="variant === 'white' ? 'light' : 'dark'"
            @click="cancelNewEntry"
          >{{ $t('Cancel') }}</span>
          <aha-button
            type="submit"
            :disabled="isAddButtonDisabled"
            @click="submitNewEntries"
          >
            <span v-if="!submitting">{{ $t('addButton') }}</span>
            <aha-spinner v-else variant="white" />
          </aha-button>
        </div>
      </div>
    </div>
    <div
      class="flex entries-box __list"
      :class="{
        invisible: isInputExpanded
      }"
    >
      <p v-if="slide.loadedSpinnerWheel && !entries.length" class="text-center text-base-60">
        {{ $t('No entries yet') }}
      </p>
      <ul class="pl-0">
        <li v-for="(item, index) in entries" :key="index">
          {{ item.name }}
          <font-awesome-icon
            v-if="isShowRemoveEntry(item)"
            v-tooltip="$t('Delete this entry')"
            icon="trash-alt"
            class="remove-entry"
            @click="clickRemoveEntry(item)"
          />
        </li>
      </ul>
    </div>
    <div class="text-sm mt-8">
      <SpinnerWheelAutoFillToggle />
    </div>

    <aha-modal
      v-if="isShowResetWheel"
      title="Update the wheel"
      size="md"
      @hide="isShowResetWheel = false"
    >
      <p>
        This will add <span class="font-bold">
          {{ pendingEntriesCount | formatTotalPoint }} new
          {{ getParticipantText }}</span> who have entered since the last spin.
      </p>
      <template #action>
        <aha-link has-margin-right @click="isShowResetWheel = false">
          <span class="font-bold">Cancel</span>
        </aha-link>
        <aha-button
          type="button"
          class="confirm-modal-ok-btn"
          :disabled="submitResetSpinnerWheel"
          @click="resetWheel"
        >
          Update <aha-spinner v-if="submitResetSpinnerWheel" variant="white" class="ml-1" />
        </aha-button>
      </template>
    </aha-modal>
  </div>
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import Socket from '@/socket'
import SlideOptionApi from '@/api/slideOption'
import SlideApi from '@/api/slide'
import Util from '@/util'

import SpinnerWheelAutoFillToggle from './SpinnerWheelAutoFillToggle.vue'

export default {
  components: {
    SpinnerWheelAutoFillToggle,
  },
  props: {
    variant: {
      type: String,
      default: 'white',
    },
    entries: {
      type: Array,
      default: () => {
        return []
      },
    },
    createNewEntry: {
      type: Function,
      default: () => {
        return null
      },
    },
    removeEntry: {
      type: Function,
      default: () => {
        return null
      },
    },
    resetSpinnerWheel: {
      type: Function,
      default: () => {
        return null
      },
    },
    isUserOwnPresentation: {
      type: Boolean,
      default: false,
    },
    pendingEntriesCount: {
      type: Number,
      default: 0,
    },
    oldEntryFromAudienceCount: {
      type: Number,
      default: 0,
    },
    oldWinner: {
      type: Object,
      default: null,
    },
    countWheelEntries: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      newEntry: '',
      submitting: false,
      submitResetSpinnerWheel: false,
      isShowResetWheel: false,
    }
  },
  computed: {
    ...mapState({
      slide: (state) => state.slideActive,
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      sharePresentation: (state) => state.sharePresentation,
    }),
    ...mapGetters(['isBackstage']),
    getParticipantText() {
      if (this.pendingEntriesCount === 1) {
        return 'participant'
      }
      return 'participants'
    },
    isAddButtonDisabled() {
      return !this.newEntry
        || this.submitting
        || this.isBackstage
        || this.sharePresentation
    },
    isInputDisabled() {
      return this.isBackstage
        || this.sharePresentation
    },
    isInputExpanded() {
      return this.newEntry
        || (this.slideActive && this.slideActive.isFocusingInput)
    },
    entriesPlaceholder() {
      return this.isInputExpanded
        ? this.$t('multipleEntriesPlaceholder')
        : this.$t('entriesPlaceholder')
    },
    isShowUpdateWheelNotice() {
      return this.oldWinner !== null
        && this.slide.metadata
        && this.slide.metadata.autoFillParticipantName
        && (this.pendingEntriesCount || this.oldEntryFromAudienceCount)
        && !this.isBackstage
        && !this.sharePresentation
    },
  },
  destroyed() {
    this._updateSlideActiveFocusingInput(false)
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    onFocusEntryInput() {
      if (!this.isInputDisabled) {
        this._updateSlideActiveFocusingInput(true)
      }
    },
    onBlurEntryInput() {
      this._updateSlideActiveFocusingInput(false)
    },
    _updateSlideActiveFocusingInput(boolean) {
      this.updateSlideActiveAction({
        isFocusingInput: boolean,
      })
    },

    cancelNewEntry() {
      this.newEntry = ''
    },

    isShowRemoveEntry(item) {
      return item.entryType === 'manual_presenter'
        && !this.isBackstage
        && !this.sharePresentation
    },

    async submitNewEntries() {
      if (this.slide.wheelStatus === 'spinning') return
      if (!this.newEntry || this.submitting) return

      let rawEntries = this.newEntry.split(/\r?\n/)
      let order = 2

      if (this.slide.SlideOptions && this.slide.SlideOptions.length) {
        order = this.slide.SlideOptions[0].order
      }

      rawEntries = rawEntries
        .filter((item) => item.trim())
        .map((entry) => {
          order /= 2

          return ({
            accessCode: this.presentation.uniqueAccessCode,
            presentationId: this.presentation.id,
            order,
            slideId: this.slide.id,
            socketId: Socket.getSocketId(),
            title: entry.trim(),
          })
        })

      const payload = {
        accessCode: this.presentation.uniqueAccessCode,
        presentationId: this.presentation.id,
        slideId: this.slide.id,
        socketId: Socket.getSocketId(),
        options: rawEntries,
      }

      this.submitting = true

      try {
        const {
          SlideOptions: slideOptions,
        } = await SlideOptionApi.createSpinnerWheelOptions(payload)
        this.newEntry = ''

        this.$store.state.slideActive.SlideOptions.unshift(...slideOptions)
        this.updateSlideActiveAction({
          SlideOptions: this.slide.SlideOptions,
        })
        this.createNewEntry(slideOptions)
      } catch {
        this.$store.dispatch('setSyncPresentation', 3)
      } finally {
        this.submitting = false
      }
    },

    async clickRemoveEntry(entry) {
      if (this.isBackstage) return
      if (this.slide.wheelStatus === 'spinning') return
      if (entry.entryType === 'manual_presenter') {
        const payload = {
          accessCode: this.presentation.uniqueAccessCode,
          deleted: true,
          presentationId: this.presentation.id,
          presenting: false,
          socketId: Socket.getSocketId(),
        }
        try {
          SlideOptionApi.updateSlideOption(entry.id, payload)
          const indexOption = this.$store.state.slideActive.SlideOptions.findIndex((item) => {
            return item.id === entry.id
          })
          if (indexOption !== -1) {
            this.$store.state.slideActive.SlideOptions.splice(indexOption, 1)
          }

          this.updateSlideActiveAction({
            SlideOptions: this.slide.SlideOptions,
          })
        } catch {
          this.$store.dispatch('setSyncPresentation', 3)
          // do notthing
        }
      }
      this.removeEntry(entry)
    },

    openPopupUpdateWheel() {
      if (this.slide.wheelStatus === 'spinning') return
      this.isShowResetWheel = true
    },

    async resetWheel() {
      if (this.slide.wheelStatus === 'spinning') return
      this.submitResetSpinnerWheel = true
      try {
        await SlideApi.updateTheWheel(this.slide.id, {
          uniqueAccessCode: this.presentation.uniqueAccessCode,
          entries: this.entries,
        })
      } catch {
        Util.showToastError(this.$i18nPresenter.t('Fail to update spinner wheel'))
      }

      this.resetSpinnerWheel()
      this.submitResetSpinnerWheel = false
      this.isShowResetWheel = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.entries-box {
  &.__header {
    padding: 12px 0;
    font-weight: bold;
    @apply text-base-80;

    &.black {
      color: #fff;
    }
    .entries-count {
      font-size: 14px;
      span {
        font-weight: normal;
        background: rgba(175, 175, 175, 0.3);
        border-radius: 4px;
        padding: 2px 6px;
        display: inline-block;
        margin-left: 5px;
        font-size: 12px;
      }
    }
  }

  .pending-count {
    margin-right: 10px;
    font-size: 12px;
    font-weight: normal;

    span {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      display: block;
      background-color: #AFAFAF;
      margin-right: 2px;
    }

    &.new-audience {
      span {
        background-color: #33C173;
      }
    }
  }

  &.__blur {
    position: absolute;
    height: 100%;
    width: 280px;
    top: 0;
    left: -14px;
    -webkit-backdrop-filter: blur(6px);
    backdrop-filter: blur(6px);
    z-index: -1;
  }

  & ::v-deep textarea {
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 14px;
    width: 100%;
    padding-right: 30px;
    height: 2rem;
    margin-top: 0;
    margin-bottom: 0;

    &.dark {
      @apply text-secondary-20 bg-transparent border border-secondary-20;
      @apply hover:border-primary-90 focus:border-primary-90;
    }

    &::placeholder {
      font-size: 14px;
    }
  }

  & ::v-deep .aha-textarea--char-counter {
    height: 2rem;
    font-size: 12px;
  }

  &.__input-entry {
    min-height: 46px;
    position: relative;

    .count {
      position: absolute;
      right: 7px;
      font-size: 14px;
      opacity: 0.6;
      top: 8px;
    }

    .aha-button {
      margin-top: -5px;
      padding: 4px 0px;
      font-size: 14px;
      min-width: 3rem;
      & ::v-deep .aha-spinner {
        margin-bottom: -5px;
      }
    }

    &::v-deep {
      .aha-textarea-wrapper.disabled {
        textarea:hover {
          border-color: #D3D7E1;
          cursor: not-allowed;
        }
      }
    }
  }

  &.__reset {
    font-size: 12px;
    .btn-update-wheel {
      @apply text-primary-90 font-bold;
      cursor: pointer;
      margin-left: 2px;

      &:hover {
        @apply text-primary-100;
      }
    }
  }

  &.__list {
    overflow-y: auto;
    height: 172px;

    p {
      margin-top: 50px;
      font-size: 14px;
    }

    ul {
      li {
        font-size: 14px;
        padding: 6px 10px;
        border-radius: 4px;
        position: relative;
        list-style-type: none;
        display: inline-block;
        width: 100%;
        text-overflow: ellipsis;
        overflow: hidden;

        &:nth-child(2n + 1) {
          background: rgba(211, 215, 225, 0.3);
        }

        .remove-entry {
          @apply text-base-60;
          position: absolute;
          right: 10px;
          top: 10px;
          display: none;
          cursor: pointer;
        }

        &:hover {
          .remove-entry {
            display: block;
            &:hover {
              opacity: 0.6;
            }
          }
        }
      }
    }
  }
}

.relative-entry-box {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  & ::v-deep {
    textarea {
      height: 32px;
      resize: none;
    }
  }

  &.expanded ::v-deep {
    textarea {
      height: 180px;
    }
  }

  &.expanded {
    .actions {
      display: flex;
      justify-content: flex-end;
    }
  }
}

.actions {
  display: none;
  margin-top: 0.5rem;

  &-secondary {
    cursor: pointer;
    color: #707070;

    &:hover {
      color: #313131;
    }

    &.dark {
      color: #D1D1D1;

      &:hover {
        color: #EBEBEB;
      }
    }
  }
}
</style>
